import React from "react";
import { useTranslation } from "react-i18next";
import {
  Event,
  Identifiable,
  List as L,
  Milestone,
  Project,
  Task,
} from "@planzone/dto";
import { useRetrieveElementConstraints } from "@planzone/view-models";

import { UseCases } from "@planzone/core";
import { useModal } from "@root/client/modal";
import { ListIcon } from "@common/listIcon";
import { MilestoneIcon } from "@common/milestoneIcon";
import { TaskIcon } from "@common/taskIcon";
import { Props } from "./types";
import styles from "./styles.scss";

function isElDone(
  el:
    | Identifiable<Project>
    | Identifiable<Task>
    | Identifiable<L>
    | Identifiable<Event>
    | Identifiable<Milestone>,
  type: "TASK" | "ACTIVITY" | "MILESTONE",
) {
  return type === "TASK"
    ? (el as Identifiable<Task>).percentComplete === 100
    : type === "MILESTONE" && (el as Identifiable<Milestone>).state === "DONE";
}

function List(props: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    constraints,
    retrieveElementConstraints,
  } = useRetrieveElementConstraints(props.elementId);
  const getConstrainedElement = (constraint: (typeof constraints)[number]) =>
    constraint.origin.uuid === props.elementId
      ? { el: constraint.target, type: constraint.targetEntityType }
      : { el: constraint.origin, type: constraint.originEntityType };
  const constrainedElements = constraints
    .filter(({ origin }) => origin.uuid === props.elementId)
    .map((constraint) => ({
      ...constraint,
      ...getConstrainedElement(constraint),
    }))
    .map((constraint) => ({
      ...constraint,
      el: {
        ...constraint.el,
        isDone: isElDone(constraint.el, constraint.type),
      },
    }));
  const constrainingElements = constraints
    .filter(({ target }) => target.uuid === props.elementId)
    .map((constraint) => ({
      ...constraint,
      ...getConstrainedElement(constraint),
    }))
    .map((constraint) => ({
      ...constraint,
      el: {
        ...constraint.el,
        isDone: isElDone(constraint.el, constraint.type),
      },
    }));

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveElementConstraints({
        elementId: props.elementId,
        elementType: props.elementType,
      });
  }, [props.elementId]);

  return (
    <div className={styles.list}>
      {constrainingElements.length > 0 && (
        <div className={styles.section}>
          <p className={styles.title}>
            {t("common.constraintsPopup.remainingConstrainedElementsTitle")}:
          </p>
          {constrainingElements.map(({ el, type, ...constraint }) => (
            <div key={constraint.uuid} className={styles.item}>
              <div
                className={styles.element}
                onClick={() =>
                  pushView({
                    key:
                      type === "ACTIVITY"
                        ? UseCases.retrieveList
                        : type === "MILESTONE"
                          ? UseCases.retrieveMilestone
                          : UseCases.retrieveTask,
                    data: (type === "ACTIVITY"
                      ? { listId: el.uuid }
                      : type === "MILESTONE"
                        ? { milestone: el }
                        : { task: el }) as any,
                  })
                }
              >
                {type === "ACTIVITY" ? (
                  <ListIcon className={styles.icon} />
                ) : type === "MILESTONE" ? (
                  <MilestoneIcon className={styles.icon} />
                ) : (
                  <TaskIcon className={styles.icon} />
                )}
                <p className={styles.name}>{el.name}</p>
              </div>
              <button
                className={styles.button}
                onClick={(e) => {
                  e.stopPropagation();
                  pushView({
                    key: UseCases.editConstraint,
                    data: {
                      constraint,
                      origin: constraint.origin,
                      target: constraint.target,
                      project: props.project,
                    },
                  });
                }}
              >
                {t("common.constraintsPopup.seeConstraint")}
              </button>
            </div>
          ))}
        </div>
      )}
      {constrainedElements.length > 0 && (
        <div className={styles.section}>
          <p className={styles.title}>
            {t("common.constraintsPopup.constrainedElementsTitle")}:
          </p>
          {constrainedElements.map(({ el, type, ...constraint }) => (
            <div
              key={constraint.uuid}
              className={styles.item}
              onClick={() =>
                pushView({
                  key:
                    type === "ACTIVITY"
                      ? UseCases.retrieveList
                      : type === "MILESTONE"
                        ? UseCases.retrieveMilestone
                        : UseCases.retrieveTask,
                  data: (type === "ACTIVITY"
                    ? { listId: el.uuid }
                    : type === "MILESTONE"
                      ? { milestone: el }
                      : { task: el }) as any,
                })
              }
            >
              <div className={styles.element}>
                {type === "ACTIVITY" ? (
                  <ListIcon className={styles.icon} />
                ) : type === "MILESTONE" ? (
                  <MilestoneIcon className={styles.icon} />
                ) : (
                  <TaskIcon className={styles.icon} />
                )}
                <p className={styles.name}>{el.name}</p>
              </div>
              <button
                className={styles.button}
                onClick={(e) => {
                  e.stopPropagation();
                  pushView({
                    key: UseCases.editConstraint,
                    data: {
                      constraint,
                      origin: constraint.origin,
                      target: constraint.target,
                      project: props.project,
                    },
                  });
                }}
              >
                {t("common.constraintsPopup.seeConstraint")}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { List };
